import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiFetch } from '../api';
import type { AccountingAccount, AccountingAccountsUpdateRequest } from '../types/models/AccountingAccount';
import type { ApiResponseAdminAccountingAccounts } from '../types/api/ApiResponseAdminAccountingAccounts';
import { errorMessageText } from '../app/Admin/Reconciliation/reconciliationLabels';

// Staff-only chart of accounts of the « Comptabilité » entries (docs/api-types/admin-accounting-accounts.md).
// JSend at HTTP 200: branch on `body.status`, never `response.ok`. A non-staff caller gets a `fail` with
// `data.msg` (no `message`); validation errors are keyed per field (`data["accounts.<role>.number"]`).

/** The French failure text of a response (its `message`, else a `fail`'s `data.msg`, else `fallback`). */
function failureText(body: ApiResponseAdminAccountingAccounts | null, fallback: string): string {
  const msg = typeof body?.data?.msg === 'string' ? body.data.msg : null;
  const raw = body?.message ?? msg;
  return raw ? errorMessageText(raw) : fallback;
}

interface UseAccountingAccountsReturn {
  accounts: AccountingAccount[] | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * `GET /admin/accounting/accounts`, read once each time `isOpen` turns true (the settings modal). It seeds
 * an editable form, so it never re-reads on its own while open — not even when `apiFetch` changes identity
 * on an OIDC silent renew, which would otherwise wipe unsaved input.
 */
export function useAccountingAccounts(isOpen: boolean): UseAccountingAccountsReturn {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [accounts, setAccounts] = useState<AccountingAccount[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current('/admin/accounting/accounts');
      const body = (await response.json().catch(() => null)) as ApiResponseAdminAccountingAccounts | null;
      if (body?.status === 'success' && Array.isArray(body.data?.accounts)) {
        setAccounts(body.data.accounts);
        return;
      }
      setError(failureText(body, 'Impossible de lire le plan comptable.'));
    } catch (err) {
      setError(err instanceof Error ? `Erreur réseau : ${err.message}` : 'Erreur réseau.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      hasFetchedRef.current = false;
      setAccounts(null);
      setError(null);
      return;
    }
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void reload();
    }
  }, [isOpen, reload]);

  return { accounts, loading, error, reload };
}

export type SaveAccountingAccountsResult =
  | { ok: true; accounts: AccountingAccount[] }
  | { ok: false; message: string; fieldErrors: Record<string, string> };

/** `PATCH /admin/accounting/accounts` — merge semantics, all-or-nothing. */
export function useSaveAccountingAccounts(): {
  save: (body: AccountingAccountsUpdateRequest) => Promise<SaveAccountingAccountsResult>;
  saving: boolean;
} {
  const apiFetch = useApiFetch();
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (payload: AccountingAccountsUpdateRequest): Promise<SaveAccountingAccountsResult> => {
      setSaving(true);
      try {
        const response = await apiFetch('/admin/accounting/accounts', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        const body = (await response.json().catch(() => null)) as ApiResponseAdminAccountingAccounts | null;
        if (body?.status === 'success' && Array.isArray(body.data?.accounts)) {
          return { ok: true, accounts: body.data.accounts };
        }
        const fieldErrors: Record<string, string> = {};
        for (const [key, value] of Object.entries(body?.data ?? {})) {
          if (key !== 'accounts' && key !== 'msg' && typeof value === 'string') {
            fieldErrors[key] = errorMessageText(value);
          }
        }
        if (typeof body?.data?.accounts === 'string') {
          fieldErrors.accounts = errorMessageText(body.data.accounts);
        }
        return {
          ok: false,
          message: failureText(body, "Impossible d'enregistrer le plan comptable. Réessayez."),
          fieldErrors,
        };
      } catch (err) {
        return {
          ok: false,
          message: err instanceof Error ? `Erreur réseau : ${err.message}` : 'Erreur réseau.',
          fieldErrors: {},
        };
      } finally {
        setSaving(false);
      }
    },
    [apiFetch],
  );

  return { save, saving };
}
